// productData.js
let cachedProducts = null;

export const initializeProducts = (products) => {
    cachedProducts = products;
};

export const getProducts = () => {
    return cachedProducts;
};

export const createUrlName = (product) => {
    const baseName = product.name.toLowerCase();
    const variant = product.variant ? `-${product.variant}` : '';
    return `${baseName}${variant}`
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '')
        .replace(/--+/g, '-');
};

export const loadCachedProducts = () => {
    try {
        const cached = localStorage.getItem('cached_products');
        if (cached) {
            let temp = JSON.parse(cached);
            cachedProducts = temp.products;
            return true;
        }
    } catch (error) {
        console.error('Error loading cached products:', error);
    }
    return false;
};

export const findProductByUrlName = (urlName) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    return cachedProducts?.find(product => product.url === urlName);
};


export const findProductById = (id) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    const formNames = cachedProducts.filter(item => item.id === id).map(item => item.form_name);
    return formNames;
};


export const getProductByStripeId = (id) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    const product = cachedProducts.filter(item => item.id === id);
    return product;
}

export const getProductById = (id) => {
    if (!cachedProducts) {
        loadCachedProducts();
    }
    const product = cachedProducts.filter(item => item.id === id);
    return product;
};

export const getProductsByCategory = (category, type = 1) => {
    
    return cachedProducts?.filter(product => 
        (product.category.primary === category || 
         product.category.secondary === category ||
         product.category.tertiary === category) &&
        product.is_prescription == type
    ) || [];
};

export const getProductsByOffer = (offer) => {
    return cachedProducts?.filter(product => 
        (product.stripe_id === offer)) || [];
};

export const getProductsByPharmacy = (pharmacy) => {
    return cachedProducts?.filter(product => product.pharmacy === pharmacy) || [];
};

export const getProductsByPriceRange = (minPrice, maxPrice) => {
    return cachedProducts?.filter(product => {
        const price = product.pricing.discounted_price;
        return price >= minPrice && price <= maxPrice;
    }) || [];
};

export const getProductVariants = (baseName) => {
    return cachedProducts?.filter(product => 
        product.name.toLowerCase() === baseName.toLowerCase()
    ) || [];
};

export const hasVariants = (productName) => {
    return (cachedProducts?.filter(product => 
        product.name.toLowerCase() === productName.toLowerCase()
    ) || []).length > 1;
};

export const getUniqueProductNames = () => {
    return [...new Set(cachedProducts?.map(product => product.name) || [])];
};

export const getVariantDetails = (product) => {
    if (!product.variant) return null;
    
    const details = {
        fullName: `${product.name} ${product.variant}`,
        variant: product.variant,
        isShipCold: product.variant.includes('*Ship Cold*'),
        strength: null,
        volume: null,
        form: null
    };

    const strengthMatch = product.variant.match(/(\d+\.?\d*)\s*(mg|mcg|%)/i);
    if (strengthMatch) {
        details.strength = {
            value: parseFloat(strengthMatch[1]),
            unit: strengthMatch[2]
        };
    }

    const volumeMatch = product.variant.match(/(\d+\.?\d*)\s*(ml|mL)/i);
    if (volumeMatch) {
        details.volume = {
            value: parseFloat(volumeMatch[1]),
            unit: volumeMatch[2]
        };
    }

    const forms = ['Cream', 'Tablet', 'Injectable', 'Pump', 'Vial'];
    forms.forEach(form => {
        if (product.variant.includes(form)) {
            details.form = form;
        }
    });

    return details;
};

export const getFullProductName = (product) => {
    return product.variant ? `${product.name} ${product.variant}` : product.name;
};

export const getDisplayName = (product, includeVariant = true) => {
    if (!includeVariant || !product.variant) {
        return product.name;
    }
    
    const cleanVariant = product.variant
        .replace('*Ship Cold*', '')
        .trim();
    
    return `${product.name} ${cleanVariant}`;
};

export const getDiscountedPrice = (product) => {
    return product.pricing.discounted_price;
};

export const getSavingsAmount = (product) => {
    return product.pricing.savings_amount;
};

export const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
    }).format(price);
};

export const getProductBenefits = (product) => {
    return product.benefits.key_points;
};

export const getProductTestimonials = (product) => {
    return product.testimonials;
};

export const getProductBullets = (product) => {
    return [
        product.product_bullets.bullet1,
        product.product_bullets.bullet2,
        product.product_bullets.bullet3,
        product.product_bullets.bullet4
    ];
};

export const getShippingInfo = (product) => {
    return {
        cost: product.pricing.shipping_cost,
        type: product.pricing.shipping_type,
        additionalCost: product.pricing.additional_cost
    };
};

export const requiresColdShipping = (product) => {
    return product.variant?.includes('*Ship Cold*') || 
           product.pricing.shipping_type === 'Overnight';
};

export const getAllCategories = () => {
    const categories = new Set();
    cachedProducts?.forEach(product => {
        if (product.category.primary) categories.add(product.category.primary);
        if (product.category.secondary) categories.add(product.category.secondary);
        if (product.category.tertiary) categories.add(product.category.tertiary);
    });
    return Array.from(categories);
};

export const sortProductsByPrice = (productList, ascending = true) => {
    return [...(productList || [])].sort((a, b) => {
        const priceA = a.pricing.discounted_price;
        const priceB = b.pricing.discounted_price;
        return ascending ? priceA - priceB : priceB - priceA;
    });
};

export const sortProductVariants = (variants, criteria = 'strength') => {
    return [...(variants || [])].sort((a, b) => {
        const detailsA = getVariantDetails(a);
        const detailsB = getVariantDetails(b);
        
        if (!detailsA || !detailsB) return 0;

        switch (criteria) {
            case 'strength':
                return (detailsA.strength?.value || 0) - (detailsB.strength?.value || 0);
            case 'volume':
                return (detailsA.volume?.value || 0) - (detailsB.volume?.value || 0);
            case 'price':
                return a.pricing.discounted_price - b.pricing.discounted_price;
            default:
                return 0;
        }
    });
};

export const searchProducts = (query) => {
    const searchTerm = query.toLowerCase();
    return cachedProducts?.filter(product => 
        product.name.toLowerCase().includes(searchTerm) ||
        product.brand_name.toLowerCase().includes(searchTerm) ||
        product.marketing.main_header.toLowerCase().includes(searchTerm) ||
        product.marketing.sub_header.toLowerCase().includes(searchTerm)
    ) || [];
};

export const getProductWithVariants = (productName) => {
    const variants = getProductVariants(productName);
    const baseProduct = variants[0];
    
    if (!baseProduct) return null;
    
    return {
        name: productName,
        baseProduct: baseProduct,
        variants: sortProductVariants(variants),
        hasMultipleVariants: variants.length > 1,
        lowestPrice: Math.min(...variants.map(v => v.pricing.discounted_price)),
        highestPrice: Math.max(...variants.map(v => v.pricing.discounted_price)),
        forms: [...new Set(variants.map(v => getVariantDetails(v)?.form).filter(Boolean))]
    };
};

export const getProductAnalytics = () => {
    if (!cachedProducts) return null;
    
    const uniqueProducts = getUniqueProductNames();
    return {
        totalProducts: cachedProducts.length,
        uniqueProducts: uniqueProducts.length,
        categoryCounts: getAllCategories().reduce((acc, category) => {
            acc[category] = getProductsByCategory(category).length;
            return acc;
        }, {}),
        pharmacyCounts: [...new Set(cachedProducts.map(p => p.pharmacy))].reduce((acc, pharmacy) => {
            acc[pharmacy] = getProductsByPharmacy(pharmacy).length;
            return acc;
        }, {}),
        priceRange: {
            min: Math.min(...cachedProducts.map(p => p.pricing.discounted_price)),
            max: Math.max(...cachedProducts.map(p => p.pricing.discounted_price)),
            average: cachedProducts.reduce((acc, p) => acc + p.pricing.discounted_price, 0) / cachedProducts.length
        },
        productsWithVariants: uniqueProducts.filter(name => hasVariants(name)).length
    };
};